import { useMemo } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import type { LegacyCaseDetail } from "../../../types/legacyCases";
import {
  createLegacyReviewValues,
  type LegacyReviewValues,
} from "../../../utils/legacyReviewForm";

interface Props {
  latest: LegacyCaseDetail;
  current: LegacyReviewValues;
  onClose: () => void;
  onLoad: () => void;
  onKeep: () => void;
}

export default function LegacyDraftConflict({
  latest,
  current,
  onClose,
  onLoad,
  onKeep,
}: Props) {
  const saved = useMemo(() => createLegacyReviewValues(latest), [latest]);
  const differences = useMemo(
    () =>
      Object.keys(current).filter(
        (key) =>
          JSON.stringify(current[key as keyof LegacyReviewValues]) !==
          JSON.stringify(saved[key as keyof LegacyReviewValues])
      ),
    [current, saved]
  );
  return (
    <Modal
      opened
      onClose={onClose}
      title={`A newer draft was saved (revision ${latest.reviewRevision})`}
      size="xl"
      centered
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Alert color="orange">
          Your unsaved edits are retained. Compare the changes before loading
          the saved draft or keeping your version for the next save.
        </Alert>
        <ScrollArea.Autosize mah="55vh">
          <Table.ScrollContainer minWidth={500}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Field</Table.Th>
                  <Table.Th>Your draft</Table.Th>
                  <Table.Th>Latest saved draft</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {differences.map((key) => (
                  <Table.Tr key={key}>
                    <Table.Td>{key.replace(/([A-Z])/g, " $1")}</Table.Td>
                    <Table.Td>
                      <Text
                        size="sm"
                        style={{
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {JSON.stringify(
                          current[key as keyof LegacyReviewValues],
                          null,
                          2
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text
                        size="sm"
                        style={{
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {JSON.stringify(
                          saved[key as keyof LegacyReviewValues],
                          null,
                          2
                        )}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </ScrollArea.Autosize>
        <Group justify="space-between">
          <Button variant="default" onClick={onClose} data-autofocus>
            Keep comparing
          </Button>
          <Group>
            <Button variant="default" onClick={onLoad}>
              Load saved draft
            </Button>
            <Button onClick={onKeep}>Keep my edits for next save</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
